"use client";

import * as React from "react";
import { signOut } from "next-auth/react";

export function AvatarMenu(props: {
  name: string | null;
  email: string;
  image: string | null;
  firstName: string | null;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        className="avatar-pill"
      >
        {props.image ? (
          <img
            src={props.image}
            alt=""
            width={26}
            height={26}
            style={{ borderRadius: "50%", display: "block" }}
          />
        ) : (
          <span className="avatar-fallback">
            {(props.firstName ?? "?").charAt(0).toUpperCase()}
          </span>
        )}
        <span className="avatar-name">{props.firstName ?? "Account"}</span>
        <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden style={{ opacity: 0.65 }}>
          <path
            d="M2 4l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <div className="avatar-menu" role="menu">
          <div className="avatar-menu-head">
            {props.image ? (
              <img
                src={props.image}
                alt=""
                width={36}
                height={36}
                style={{ borderRadius: "50%" }}
              />
            ) : null}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{props.name ?? props.email}</span>
              <span style={{ fontSize: 11, opacity: 0.6 }}>{props.email}</span>
            </div>
          </div>
          <div className="avatar-menu-divider" />
          <button
            type="button"
            role="menuitem"
            className="avatar-menu-item"
            onClick={() => void signOut({ callbackUrl: "/" })}
          >
            <span aria-hidden>↩</span>
            <span>Sign out</span>
          </button>
        </div>
      ) : null}
      <style>{`
        .avatar-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 4px 12px 4px 4px;
          background: var(--jobu-input-bg);
          color: var(--jobu-text);
          border: 1px solid var(--jobu-input-border);
          border-radius: 999px;
          cursor: pointer;
          font: inherit; font-size: 13px; font-weight: 500;
          transition: background 150ms, border-color 150ms;
        }
        .avatar-pill:hover { background: var(--jobu-row-bg-hover); border-color: var(--jobu-row-border-hover); }
        .avatar-fallback {
          width: 26px; height: 26px; border-radius: 50%;
          background: linear-gradient(135deg, #5b8def 0%, #7c5bef 100%);
          display: grid; place-items: center;
          color: #fff; font-size: 11px; font-weight: 700;
        }
        .avatar-name { padding: 0 2px; }
        .avatar-menu {
          backdrop-filter: blur(20px) saturate(140%);
          -webkit-backdrop-filter: blur(20px) saturate(140%);
          position: absolute; right: 0; top: calc(100% + 8px);
          min-width: 240px;
          background: var(--jobu-card);
          border: 1px solid var(--jobu-card-border);
          border-radius: 12px;
          padding: 6px;
          backdrop-filter: blur(20px) saturate(140%);
          -webkit-backdrop-filter: blur(20px) saturate(140%);
          box-shadow: var(--jobu-card-shadow);
          z-index: 50;
          animation: avatar-menu-in 200ms cubic-bezier(.2,1.4,.6,1);
          transform-origin: top right;
        }
        @keyframes avatar-menu-in {
          from { opacity: 0; transform: scale(0.92) translateY(-4px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .avatar-menu-head {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 10px 12px;
        }
        .avatar-menu-divider {
          height: 1px;
          background: var(--jobu-row-bg-hover);
          margin: 0 4px 6px;
        }
        .avatar-menu-item {
          width: 100%;
          display: flex; align-items: center; gap: 10px;
          padding: 9px 12px;
          background: transparent;
          color: inherit;
          border: 0; border-radius: 8px;
          cursor: pointer; font: inherit; font-size: 13px;
          text-align: left;
          transition: background 120ms;
        }
        .avatar-menu-item:hover { background: var(--jobu-row-bg-hover); }
      `}</style>
    </div>
  );
}
